import { buildDayValidityMask, maskMetric } from '@emfit/shared-store';
import type {
  DailySleepMetrics,
  GroupEntry,
} from '../types/charts/GroupDashboardData';

/**
 * Metrics where 0 is a real reading on a night that has data — a resident can
 * genuinely have zero bed exits. On a no-data night these are still nulled,
 * since the mask is applied first.
 */
const COUNT_METRICS = [
  'bedexit_count',
  'tossnturn_hourly',
  'tossnturn_hourly_alt',
  'tossnturn_count',
  'tossnturn_count_alt',
  'pir_activity',
] as const satisfies readonly (keyof DailySleepMetrics)[];

/**
 * Metrics where 0 can only mean "not measured" — a sleeping resident never has
 * a heart rate of 0, and a regularity index of exactly 0 is the backend's way
 * of saying it could not compute one.
 */
const CONTINUOUS_METRICS = [
  'sri',
  'tst',
  'min_hr',
  'max_hr',
  'avg_hr',
  'min_rr',
  'max_rr',
  'avg_rr',
  'duration_in_bed',
  'btri',
] as const satisfies readonly (keyof DailySleepMetrics)[];

/**
 * Nulls out metrics for days the device has no data for, so charts draw a gap
 * instead of a point at zero. See `buildDayValidityMask` for the rule.
 *
 * Returns a new entry; `daily.bedtime_data`, `daily.heatmaps` and everything
 * outside the masked metrics are carried over untouched.
 */
export function applyDayValidityMask(entry: GroupEntry): GroupEntry {
  const { daily } = entry.sleep;
  const mask = buildDayValidityMask(daily.duration_in_bed, daily.tst);

  // Only touch metrics the response actually carries — masking must not
  // invent keys the backend left out.
  const masked: Partial<DailySleepMetrics> = {};
  for (const key of CONTINUOUS_METRICS) {
    if (Array.isArray(daily[key])) masked[key] = maskMetric(daily[key], mask);
  }
  for (const key of COUNT_METRICS) {
    if (Array.isArray(daily[key])) {
      masked[key] = maskMetric(daily[key], mask, { zeroIsMissing: false });
    }
  }

  return {
    ...entry,
    sleep: {
      ...entry.sleep,
      daily: { ...daily, ...masked },
    },
  };
}
